/**
 * ChatWindow component
 *
 * Main chat container with messages, input, and action previews.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useChat, useSessions, useAuth } from '../../hooks';
import { Message } from './Message';
import { InputBar } from './InputBar';
import { ActionPreview } from './ActionPreview';
import { WelcomeScreen } from './WelcomeScreen';

export function ChatWindow() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, login, user, primaryChart } = useAuth();
  const {
    messages,
    isSending,
    isStreaming,
    toolStatus,
    actionPreview,
    feedback,
    send,
    confirm,
    cancelPreview,
    onFeedback,
  } = useChat();

  const { currentSessionUuid, isSessionLoading } = useSessions();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasHandledQueryRef = useRef(false);

  // Handle ?q= query param from timeline "Ask Lucy" button
  useEffect(() => {
    const question = searchParams.get('q');
    if (question && currentSessionUuid && !isSessionLoading && !hasHandledQueryRef.current) {
      hasHandledQueryRef.current = true;
      // Clear the query param from URL
      setSearchParams({});
      // Send the question
      send(question);
    }
  }, [searchParams, currentSessionUuid, isSessionLoading, send, setSearchParams]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, actionPreview]);

  const handleConfirm = () => {
    if (actionPreview) {
      confirm(actionPreview);
    }
  };

  const handleSuggest = useCallback((text: string) => {
    send(text);
  }, [send]);

  // Show landing page for unauthenticated users
  if (!isAuthenticated) {
    return (
      <div className="landing-page" data-testid="landing-page">
        <div className="landing-hero">
          <div className="landing-graphic">
            <div className="zodiac-wheel">
              <span className="zodiac-symbol">♈</span>
              <span className="zodiac-symbol">♉</span>
              <span className="zodiac-symbol">♊</span>
              <span className="zodiac-symbol">♋</span>
              <span className="zodiac-symbol">♌</span>
              <span className="zodiac-symbol">♍</span>
              <span className="zodiac-symbol">♎</span>
              <span className="zodiac-symbol">♏</span>
              <span className="zodiac-symbol">♐</span>
              <span className="zodiac-symbol">♑</span>
              <span className="zodiac-symbol">♒</span>
              <span className="zodiac-symbol">♓</span>
            </div>
            <div className="landing-moon">☽</div>
          </div>

          <h1 className="landing-title">
            <span className="title-accent">Lucy</span>
            <span className="title-sub">AI Astrolog</span>
          </h1>

          <p className="landing-tagline">
            Postavi pitanje, dobij astrološki odgovor. Bez čekanja.
          </p>

          <button className="landing-cta" onClick={login} data-testid="login-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Započni besplatno
          </button>
        </div>

        <div className="landing-features">
          <div className="feature-card">
            <div className="feature-icon">🌙</div>
            <h3>Natalna karta</h3>
            <p>Detaljno tumačenje tvoje natalne karte sa planetarnim pozicijama i aspektima.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔮</div>
            <h3>Dnevni transiti</h3>
            <p>Saznaj kako trenutne planetarne pozicije utiču na tebe.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💫</div>
            <h3>Sinastrijа</h3>
            <p>Uporedi karte i otkrij astrologiju vašeg odnosa.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no session selected
  if (!currentSessionUuid) {
    return (
      <div className="chat-area" data-testid="chat-area">
        <div className="empty-state">
          <div className="welcome-logo">✨</div>
          <h2>Dobrodošla/Dobrodošao!</h2>
          <p>Izaberi postojeću sesiju ili kreiraj novu da započneš razgovor sa Lucy.</p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isSessionLoading) {
    return (
      <div className="chat-area" data-testid="chat-area">
        <div className="loading-state">
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p>Učitavam sesiju...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-area" data-testid="chat-area">
      <div className="messages" data-testid="messages-container">
        {messages.length === 0 ? (
          <WelcomeScreen
            user={user}
            primaryChart={primaryChart}
            onSuggest={handleSuggest}
          />
        ) : (
          messages.map((msg, index) => (
            <Message
              key={msg.id || `${msg.created_at}-${index}`}
              message={msg}
              feedback={msg.id ? feedback[msg.id] : null}
              onFeedback={onFeedback}
            />
          ))
        )}

        {/* Streaming indicator with status label */}
        {isStreaming && (
          <div className="streaming-status">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
            {toolStatus && <p className="tool-status">{toolStatus}</p>}
          </div>
        )}

        {/* Action preview */}
        {actionPreview && (
          <ActionPreview
            preview={actionPreview}
            onConfirm={handleConfirm}
            onCancel={cancelPreview}
            isLoading={isSending}
          />
        )}

        <div ref={messagesEndRef} />
      </div>

      <InputBar
        onSend={send}
        disabled={isSending}
        sessionUuid={currentSessionUuid || undefined}
        hasMessages={messages.length > 0}
      />
    </div>
  );
}

export default ChatWindow;
